import React, { useMemo } from 'react';

import { css, cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { SmartLinkIconRenderer } from './SmartLinkIconRenderer.tsx';
import {
	type BylineConnectionIcon,
	getBylineConnectionIcons,
} from './utils/getBylineConnectionIcons.ts';
import type { EnhancedSmartLink } from './utils/smartLinkTypes.ts';

const styles = cssMap({
	stack: {
		display: 'inline-flex',
		alignItems: 'center',
		flexShrink: 0,
		marginInlineEnd: token('space.050'),
		isolation: 'isolate',
	},
});

const iconTileStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	boxSizing: 'border-box',
	position: 'relative',
	width: '16px',
	height: '16px',
	backgroundColor: token('elevation.surface'),
	borderColor: token('color.border'),
	borderStyle: 'solid',
	borderWidth: token('border.width'),
	borderRadius: token('radius.small'),
});

const firstIconTileStyles = css({
	zIndex: 300,
});

const secondIconTileStyles = css({
	marginInlineStart: token('space.negative.050'),
	zIndex: 200,
	rotate: '6deg',
});

const thirdIconTileStyles = css({
	marginInlineStart: token('space.negative.050'),
	zIndex: 100,
});

export type BylineConnectionIconStackProps = {
	links: EnhancedSmartLink[];
	additionalIcons?: BylineConnectionIcon[];
};

export const BylineConnectionIconStack = ({
	links,
	additionalIcons,
}: BylineConnectionIconStackProps): React.JSX.Element | null => {
	const icons = useMemo(
		() => getBylineConnectionIcons(links, additionalIcons),
		[links, additionalIcons],
	);

	if (icons.length === 0) {
		return null;
	}

	return (
		<span css={styles.stack} aria-hidden="true">
			{icons.map((icon, index) => {
				switch (index) {
					case 0:
						return (
							<span key={icon.key} css={[iconTileStyles, firstIconTileStyles]}>
								<SmartLinkIconRenderer icon={icon.icon} size="stack" />
							</span>
						);
					case 1:
						return (
							<span
								key={icon.key}
								css={[iconTileStyles, secondIconTileStyles]}
								data-rotation-enabled
							>
								<SmartLinkIconRenderer icon={icon.icon} size="stack" />
							</span>
						);
					case 2:
						return (
							<span key={icon.key} css={[iconTileStyles, thirdIconTileStyles]}>
								<SmartLinkIconRenderer icon={icon.icon} size="stack" />
							</span>
						);
					default:
						return null;
				}
			})}
		</span>
	);
};
